import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import useNotification from "../components/notifications/useNotifications";
import { useAuth } from "../contexts/AuthContext";
import useApiHelper from "../hooks/useApiHelper";
import {
  ICreateSalesQuoteRequest,
  ILoadSalesQuoteResponseEnvelope,
  ISalesQuoteResponse,
} from "../models/SalesQuote";
import apiConfig from "../utils/apiConfig";

export const SALES_QUOTES_PAGED_URL = "/salesquote/paged";

export default function useSalesQuotes(salesQuoteId?: string, componentName = "useSalesQuotes") {
  const { get, post } = useApiHelper(apiConfig(componentName));
  const queryClient = useQueryClient();
  const { userId } = useAuth();
  const { addNotification } = useNotification();

  // Saving, loading and cancelling all change what the basket drawer and the list show.
  const invalidate = () => {
    queryClient.invalidateQueries(["salesQuotes"]);
    queryClient.invalidateQueries(["pagedTable", SALES_QUOTES_PAGED_URL]);
    queryClient.invalidateQueries(["basket", userId]);
  };

  // The API's refusals ("Quote is cancelled and can't be loaded") are the useful part of the error.
  const onError = (error: unknown) => {
    addNotification({
      variant: "error",
      primaryText: error instanceof Error ? error.message : "Something went wrong",
    });
  };

  const salesQuote = useQuery({
    queryKey: ["salesQuotes", salesQuoteId],
    queryFn: () => get<ISalesQuoteResponse>(`/salesquote/${salesQuoteId}`),
    enabled: !!salesQuoteId,
  });

  const create = useMutation(
    (request: ICreateSalesQuoteRequest) => post<ICreateSalesQuoteRequest, ISalesQuoteResponse>("/salesquote", request),
    { onSuccess: invalidate, onError }
  );

  const load = useMutation(
    (id: string) => post<null, ILoadSalesQuoteResponseEnvelope>(`/salesquote/${id}/load`, null),
    { onSuccess: invalidate, onError }
  );

  const cancel = useMutation(
    (id: string) => post<null, ISalesQuoteResponse>(`/salesquote/${id}/cancel`, null),
    { onSuccess: invalidate, onError }
  );

  return {
    salesQuote,
    create,
    load,
    cancel,
  };
}
